import { useSelector } from 'react-redux';
import SparkMD5 from 'spark-md5';

import { RootState } from '$store';
import UploadInput from '~components/generic/elements/Upload/UploadInput';
import useAppDispatch from '~hooks/useAppDispatch';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import { upload } from '~modules/media-library/controller/thunks/contribute';
import styles from '../UploadAssetsForm.module.css';

interface UploaderProps {
  multiple: boolean;
}

const Uploader = ({ multiple }: UploaderProps) => {
  const data = useSelector((store: RootState) => store.contribute);
  const { additionalSettings } = useMediaLibrarySettings();

  const dispatch = useAppDispatch();

  const readFirst50KB = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;

      const blob = file.slice(0, 50000);
      reader.readAsArrayBuffer(blob);
    });
  };

  const handleUpload = async (files: File[]) => {
    const uniqueFilesWithHashes: { file: File; hash: string }[] = [];
    const checksums: Set<string> = new Set();

    for (const file of files) {
      const first50kBytes = await readFirst50KB(file);
      const prefixedContent = `${file.size}_${first50kBytes}`;
      const checksum: string = SparkMD5.hashBinary(prefixedContent);

      if (!checksums.has(checksum)) {
        checksums.add(checksum);
        uniqueFilesWithHashes.push({ file, hash: checksum });
      }
    }

    await dispatch(upload({ files: uniqueFilesWithHashes }));
  };

  const mimeTypes = additionalSettings?.upload?.main_mime_types ?? [];

  return (
    <div>
      <UploadInput
        imageRatio={false}
        overlayContentClassName={styles.overlayContent}
        containerClassName={styles.containerClassName}
        progress={data.progress}
        onUpload={handleUpload}
        multiple={multiple}
        options={{
          mimeTypes: mimeTypes,
        }}
      />
    </div>
  );
};

export default Uploader;
